import type { ExtensionPayload, ExtensionViewData } from '@atlassian/forge-ui-types';
import { fg } from '@atlaskit/platform-feature-flags';
import type { ThemeState } from '@atlaskit/tokens';

import type { Extension } from '../../../web-client';
import type { ResolverContext, ViewContext } from '../types';

/**
 * Constructs the permissions object from scopes and egress data
 * Based on the logic from xen-invocation-service
 */
export const constructPermissions = (
	scopes: string[] = [],
	egress: Array<{
		addresses: string[];
		category?: string;
		inScopeEUD?: boolean;
		type: string;
	}> = [],
): ViewContext['permissions'] => {
	// Create a map to store egress data by type then loop through the map to populate the external permissions object
	const egressMap = new Map<string, string[]>();
	egress.forEach(({ type, addresses }) => {
		egressMap.set(type, addresses);
	});

	// Construct external permissions from the map
	const externalPermissions = {
		fetch: {
			backend: egressMap.get('FETCH_BACKEND_SIDE') ?? [],
			client: egressMap.get('FETCH_CLIENT_SIDE') ?? [],
		},
		fonts: egressMap.get('FONTS') ?? [],
		styles: egressMap.get('STYLES') ?? [],
		frames: egressMap.get('FRAMES') ?? [],
		images: egressMap.get('IMAGES') ?? [],
		media: egressMap.get('MEDIA') ?? [],
		scripts: egressMap.get('SCRIPTS') ?? [],
	};

	return {
		scopes,
		external: externalPermissions,
	};
};

export const getViewContext = ({
	resolverContext,
	accountId,
	extension,
	timezone,
	locale,
	location,
	extensionPayload,
	extensionViewData,
	theme,
	surfaceColor,
}: {
	accountId: string | undefined;
	extension: Extension;
	extensionPayload?: ExtensionPayload;
	extensionViewData?: ExtensionViewData;
	locale: string;
	location?: string;
	resolverContext: ResolverContext;
	surfaceColor?: string | null;
	theme?: Partial<ThemeState> | null;
	timezone: string;
}): ViewContext => {
	// Combine the static macro extension data with the dynamic extension view data (macro body) into a single macro object
	const macro = {
		...resolverContext.extension?.macro,
		...extensionViewData?.macro,
		...extensionPayload?.macro,
	};

	/* adding hasAccess separately instead of using userAccess as a whole is to avoid appending __typename in the context */
	const userAccess = extension.userAccess ?? { enabled: false, hasAccess: false };

	// Construct permissions object from scopes and egress
	const permissions = constructPermissions(extension?.scopes || [], extension?.egress || []);

	return {
		...resolverContext,
		extension: {
			...resolverContext.extension,
			...extensionViewData,
			...extensionPayload,
			...(Object.keys(macro).length > 0 ? { macro } : {}),
			location:
				extension.type === 'jira:globalBackgroundScript' &&
				fg('ditto_fcf_jira_globalbackgroundscript')
					? ''
					: location,
		},
		accountId,
		license: extension?.license || null,
		timezone,
		locale,
		theme,
		surfaceColor,
		userAccess,
		permissions,
	};
};
